import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { cloneDeep, map, upperFirst } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Company,
  ISitemateTaskDefinitionUpdate,
  ISitemateTaskDefinitionUrl,
  SitemateRecordStatuses,
  SitemateTaskTypes,
  Team,
} from '@site-mate/dashpivot-shared-library';

import { TemplateSelectorViewTypes } from 'app/apps/templates-library/templates-selector/template-selector-view-types.enum';
import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { LinkCreateStatus } from 'app/shared/model/sitemate/link-status.enum';
import { TaskCreationMessage } from 'app/shared/model/sitemate/task-creation-message.model';
import { TaskCreationMode } from 'app/shared/model/sitemate/task-creation-mode.enum';
import { ISitemateTaskDefinitionWeb } from 'app/shared/model/task-definition.model';
import { TemplateWeb } from 'app/shared/model/template.model';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { TaskService } from 'app/shared/service/task.service';

@Component({
  selector: 'cc-template-links-modal-update',
  templateUrl: './template-links-modal-update.component.html',
  styleUrls: [
    '../../templates-library/templates-selector/templates-selector-common.scss',
    './template-links-modal-update.component.scss',
  ],
})
export class TemplateLinksModalUpdateComponent implements OnDestroy, OnInit {
  private readonly destroy$ = new Subject<void>();

  @Output() readonly closeModal = new EventEmitter<void>();
  @Output() readonly taskUpdated = new EventEmitter<TaskCreationMessage>();

  readonly linkCreateStatus = { ...LinkCreateStatus };

  @Input() company?: Company;
  @Input() taskDefinition: ISitemateTaskDefinitionUrl;
  @Input() taskType: SitemateTaskTypes;
  @Input() team: Team;
  @Input() templates: TemplateWeb[];
  @Input() accessType = 'public';
  @Input() isLoading = false;
  @Input() unarchiveLink = false;

  title = '';
  selectedTemplates: TemplateWeb[] = [];
  isValid = false;
  validName = true;
  isDefaultTextColor = false;
  currentStatus = LinkCreateStatus.Initial;
  originalTaskDefinition: ISitemateTaskDefinitionUrl;
  originalSelectedTemplateIds: string[];
  templateSelectorViewTypes = TemplateSelectorViewTypes;

  constructor(
    private readonly errorHandler: ErrorHandler,
    private readonly i18nService: TmpI18NService,
    private readonly taskService: TaskService,
  ) {}

  ngOnInit(): void {
    this.originalTaskDefinition = cloneDeep(this.taskDefinition);
    this.originalSelectedTemplateIds = map(
      this.templates.filter((template) => template.selected),
      'id',
    );
    this.setTitle();
    this.onContentValidityChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onContentValidityChange(): void {
    this.selectedTemplates = this.getSelectedTemplates();
    this.isValid = this.hasValidData();
    this.validName = this.taskDefinition.name.trim().length > 0;
    this.isDefaultTextColor = this.selectedTemplates.length > 0;
  }

  onUpdateLink(): void {
    if (this.isValid) {
      this.updateLink();
    }
  }

  updateLink(): void {
    const { id: teamId, path } = this.team;
    this.currentStatus = LinkCreateStatus.GeneratingLink;

    this.taskService
      .updateTaskDefinitions(teamId, this.taskDefinition.url, {
        name: this.taskDefinition.name,
        archived: false,
        path,
        taskType: this.taskType,
        templates: this.getSelectedTemplates().map(this.selectedTemplateWithId.bind(this)),
        access: this.accessType,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ({ taskDefinitions: rawTaskDefinitions, warnings }) => {
          const taskDefinition = this.convertTaskDefinitionsToTaskWeb(rawTaskDefinitions);
          const mode = this.unarchiveLink ? TaskCreationMode.Restored : TaskCreationMode.Updated;
          this.taskUpdated.emit({ taskDefinition, warnings, mode });
        },
        (error) => {
          this.currentStatus = LinkCreateStatus.ErrorEditingLink;
          this.errorHandler.handle(error);
        },
      );
  }

  onReset(): void {
    this.resetState();
    this.currentStatus = LinkCreateStatus.Initial;
  }

  onCloseModal(): void {
    this.closeModal.emit();
  }

  private setTitle(): void {
    const typeTitleKey = this.taskType.split('-').map(upperFirst).join('');
    const modificationType = this.unarchiveLink ? 'Unarchive' : 'Update';
    this.title = this.i18nService.getMessage(`templateLinks${modificationType}${typeTitleKey}Title`);
  }

  private hasValidData(): boolean {
    return this.taskDefinition.name.trim().length > 0 && this.selectedTemplates.length > 0;
  }

  private getSelectedTemplates(): TemplateWeb[] {
    return this.templates.filter((template) => template.selected);
  }

  private resetState(): void {
    this.taskDefinition = cloneDeep(this.originalTaskDefinition);
    this.templates.forEach((template) => {
      template.selected = this.originalSelectedTemplateIds.includes(template.id);
    });
    this.onContentValidityChange();
  }

  private selectedTemplateWithId(template: TemplateWeb): ISitemateTaskDefinitionUpdate['templates'][0] {
    return {
      templateId: template.id,
      taskDefinitionId: this.taskDefinition.templateMetadata.find((metadata) => metadata._id === template.id)
        ?.taskDefinitionId,
    };
  }

  private convertTaskDefinitionsToTaskWeb(
    taskDefinitions: ISitemateTaskDefinitionUrl[],
  ): ISitemateTaskDefinitionWeb {
    const [firstTaskDefinition] = taskDefinitions;
    const allMetadata = taskDefinitions.reduce<ISitemateTaskDefinitionUrl['templateMetadata']>(
      (carry, item) => carry.concat(...item.templateMetadata),
      [],
    );

    return {
      url: firstTaskDefinition.url,
      name: firstTaskDefinition.name,
      status: SitemateRecordStatuses.Open,
      taskType: this.taskType,
      urlLabel: this.taskService.removeSchemeFromUrl(firstTaskDefinition.url),
      templateMetadata: allMetadata,
    };
  }
}
